import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Skeleton, Stack } from "@vapor/react-material";
import Grid from "@vapor/react-material/Grid";
import Card from "@vapor/react-material/Card";
import CardActionArea from "@vapor/react-material/CardActionArea";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@onefront/react-sdk";
import { useDownloadDocument } from "../../newDocuments/hooks/api-hooks/useDownloadADocument";
import { useDocumentList } from "../state/useDocumentList";
import SeenUnseenCell from "./table-cells/SeenUnseenCell";
import { usePatchChangeDocumentStatus } from "../../../core/usecases/use-patch-change-document-status/usePatchChangeDocumentStatus";
import DateCell from "./table-cells/DateCell";
import ActionCell from "./table-cells/ActionCell";
import FileInfoCell from "./table-cells/FileInfoCell";
import DeletedDataTrashbinCell from "./table-cells/DeletedDataTrashbinCell";

type DocumentSliceProps = {
  doc: any;
  handleDocumentsDelete: (documentId?: string[]) => void;
  handleDocumentRestore: (documentId: string[]) => void;
  handleMoveToBin: (documentId: string[]) => void;
  isDeleteDocumentsLoading: boolean;
  isMoveToBinLoading: boolean;
  restoreLoading: boolean;
  workspaceId: string;
  documentsApiLoading: boolean;
};

const DocumentSlice = React.memo(
  ({
    doc,
    handleDocumentsDelete,
    handleDocumentRestore,
    handleMoveToBin,
    isDeleteDocumentsLoading,
    isMoveToBinLoading,
    documentsApiLoading,
    restoreLoading,
    workspaceId,
  }: DocumentSliceProps) => {
    const [isDocumentSelected, setIsDocumentSelected] =
      useState<boolean>(false);
    const [documentType, setDocumentType] = useState<string>();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const stackRef = useRef<HTMLDivElement | null>(null);
    const open = Boolean(anchorEl);
    const { t } = useTranslation("DOCS");
    const navigate = useNavigate();

    const isFatture = useMemo(() => doc?.einvoice === true, [doc]);
    // function to format date
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      const day = date.getDate();
      const year = date.getFullYear();
      const monthIndex = date.getMonth();

      const months = t("date.months", { returnObjects: true });
      const abbreviatedMonth = months[monthIndex].slice(0, 3);

      return `${day} ${abbreviatedMonth} ${year}`;
    };

    const formattedCreatedOnDate = formatDate(doc?.createdOn);
    const formattedTrashedOnDate = formatDate(doc?.trashedOn);
    const handleClick = (event) => {
      isFatture ? setAnchorEl(event.currentTarget) : setDocumentType("ASSOSW");
    };

    const {
      addDocument,
      documentDownloaded,
      documentFilterOption,
      documentList,
      loading,
      removeSelectedDocument,
      selectAllDocuments,
      setIndeterminateCheck,
    } = useDocumentList();

    const {
      fetch: changeDocumentStatus,
      loading: loadingChangeDocumentStatus,
    } = usePatchChangeDocumentStatus();

    const { fetch } = useDownloadDocument(
      doc.documentId,
      workspaceId,
      doc.fileName,
      documentType,
      isFatture
    );

    useEffect(() => {
      if (documentType === "ASSOSW" || documentType === " ") {
        fetch();
      }
      // if this is true all documents are checked
      if (selectAllDocuments) {
        setIsDocumentSelected(true);
      } else {
        setIsDocumentSelected(false);
      }

      if (documentDownloaded) {
        setIsDocumentSelected(false);
      }
      //if we delete the rows we make sure that it will be unselected
      const documentExists = documentList.find(
        (docList) => docList.documentId === doc.documentId
      );
      if (!documentExists) {
        setIsDocumentSelected(false);
      }
      if (!documentFilterOption) {
        setAnchorEl(null);
      }
      if (documentFilterOption !== "CESTINO") {
        setAnchorEl(null);
      }
    }, [documentType, selectAllDocuments, documentDownloaded]);

    const handleBoxChange = useCallback(() => {
      // State menagment for documents based who is clicked or no (state/useDocumentList)
      if (!isDocumentSelected) {
        addDocument(doc);
      } else {
        removeSelectedDocument(doc);
      }
      setIsDocumentSelected(!isDocumentSelected);
      setIndeterminateCheck();
    }, [
      isDocumentSelected,
      addDocument,
      removeSelectedDocument,
      doc,
      setIndeterminateCheck,
    ]);

    const handleClickModal = () => {
      setAnchorEl(stackRef.current);
    };

    const handleCloseActionsPopup = () => {
      setAnchorEl(null);
    };

    return documentsApiLoading ? (
      <Stack spacing={1}>
        <Skeleton variant="rectangular" height={64} />
      </Stack>
    ) : (
      <Card ref={stackRef}>
        <Grid container>
          <CardActionArea
            onClick={() => {
              if (!loading) {
                navigate(`/document-details/${doc.documentId}/${workspaceId}`);
              }
            }}
            sx={{
              alignItems: "center",
              background: `${isDocumentSelected ? "#DBF3FF" : "white"}`,
              display: "flex",
              flexWrap: "nowrap",
              justifyContent: "space-between",
              minHeight: "68px",
              "&:hover": {
                background: `${isDocumentSelected ? "#DBF3FF" : "#F2F6F8"}`,
              },
            }}
          >
            <Grid alignItems="center" item sm={7} md={6}>
              <FileInfoCell
                fileName={doc.fileName}
                handleBoxChange={handleBoxChange}
                isDocumentSelected={isDocumentSelected}
                title={doc.title}
              />
            </Grid>
            <Grid alignItems="center" display="flex" item sm={3} md={4}>
              <DeletedDataTrashbinCell
                formattedDate={
                  documentFilterOption === "CESTINO"
                    ? formattedTrashedOnDate
                    : null
                }
              />
              <DateCell formattedDate={formattedCreatedOnDate} />
              <SeenUnseenCell status={doc?.status} />
            </Grid>
            <Grid item sm={2} md={2}>
              <Grid container>
                {documentFilterOption === "CESTINO" && <Grid item xs={8} />}
                <Grid item xs={documentFilterOption === "CESTINO" ? 4 : 12}>
                  <Stack
                    alignItems="center"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      background: isDocumentSelected ? "#DBF3FF" : "#F2F6F8",
                      "&:hover": {
                        background: isDocumentSelected ? "#DBF3FF" : "#F2F6F8",
                      },
                    }}
                  >
                    <ActionCell
                      anchorEl={anchorEl}
                      changeDocumentStatus={changeDocumentStatus}
                      doc={doc}
                      handleClick={handleClick}
                      handleClickModal={handleClickModal}
                      handleCloseActionsPopup={handleCloseActionsPopup}
                      handleDocumentsDelete={handleDocumentsDelete}
                      handleDocumentRestore={handleDocumentRestore}
                      handleMoveToBin={handleMoveToBin}
                      isDeleteDocumentsLoading={isDeleteDocumentsLoading}
                      isMoveToBinLoading={isMoveToBinLoading}
                      loadingChangeDocumentStatus={loadingChangeDocumentStatus}
                      open={open}
                      restoreLoading={restoreLoading}
                      workspaceId={workspaceId}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          </CardActionArea>
        </Grid>
      </Card>
    );
  }
);
export default DocumentSlice;
